import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, CheckCircle2, Circle, Clock, AlertTriangle, ExternalLink,
  Rocket, Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LaunchChatWidget from "@/components/LaunchChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_ICON = {
  complete: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
  in_progress: <Clock className="w-5 h-5 text-amber-500 animate-pulse" />,
  blocked: <AlertTriangle className="w-5 h-5 text-rose-600" />,
  pending: <Circle className="w-5 h-5 text-slate-300" />,
};
const STATE_TONE = {
  complete: "border-emerald-200 bg-emerald-50/30",
  in_progress: "border-amber-300 bg-amber-50",
  blocked: "border-rose-300 bg-rose-50",
  pending: "border-slate-200 bg-white",
};

export default function LaunchStatusPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "My Launch — Live Portal | WebsiteTech";
    if (!token) return;
    axios.get(`${API}/launch/tracker/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Portal not found. Double-check your link."));
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="launch-status-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {!data && !err && (
          <div className="py-24 flex items-center justify-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your launch…
          </div>
        )}
        {err && (
          <div className="p-8 rounded-2xl bg-white ring-1 ring-slate-200 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900">Portal not found</h1>
            <p className="text-slate-500 text-sm mt-1">{err}</p>
            <Link to="/launch" className="mt-5 inline-flex items-center gap-1 text-sky-600 hover:underline">
              Start a new application →
            </Link>
          </div>
        )}
        {data && (
          <>
            {/* Header */}
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 text-white p-6 sm:p-8">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-500/20 border border-orange-400/30 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-orange-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-orange-300">Live Launch Portal</div>
                  <h1 className="mt-1 text-2xl sm:text-3xl font-bold" data-testid="launch-business-name">
                    {data.business_name || "Your business"}
                  </h1>
                  {data.location && (
                    <p className="text-sm text-slate-300 mt-0.5">{data.location} · {data.vertical || "your industry"}</p>
                  )}
                </div>
                {data.ai_readiness_score != null && (
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-orange-300">AI Readiness</div>
                    <div className="text-2xl font-bold">{data.ai_readiness_score}<span className="text-sm text-slate-400"> / 100</span></div>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div className="mt-6">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-orange-300 font-bold mb-1.5">
                  <span>Launch progress</span>
                  <span data-testid="launch-progress-pct">{data.progress_pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-800/60 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 via-orange-400 to-amber-300 transition-all duration-500"
                       style={{ width: `${data.progress_pct}%` }} />
                </div>
              </div>

              {data.preview_available && (
                <Link to={`/launch/preview/${data.public_token}`}
                       className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 font-bold text-sm px-4 py-2 hover:bg-orange-50 transition-colors"
                       data-testid="launch-open-preview-btn">
                  <Sparkles className="w-4 h-4" /> Open my homepage preview <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {/* Milestone timeline */}
            <div className="mt-8" data-testid="launch-milestones">
              <h2 className="text-lg font-bold text-slate-900 mb-3">
                Your 14-milestone journey
              </h2>
              <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
                {data.milestones.map((m) => (
                  <li key={m.key}
                       className={`ml-4 rounded-xl border p-4 ${STATE_TONE[m.state] || STATE_TONE.pending}`}
                       data-testid={`milestone-${m.key}`}>
                    <span className="absolute -left-[13px] mt-1 bg-white rounded-full">
                      {STATE_ICON[m.state] || STATE_ICON.pending}
                    </span>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{m.label}</div>
                      <span className={`text-[10px] uppercase tracking-widest font-bold ${
                        m.state === "complete" ? "text-emerald-700"
                        : m.state === "in_progress" ? "text-amber-700"
                        : m.state === "blocked" ? "text-rose-700"
                        : "text-slate-400"}`}>
                        {m.state.replace("_", " ")}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{m.detail}</p>
                    {m.note && (
                      <p className="text-xs italic text-slate-500 mt-2 bg-white/60 rounded-lg p-2">
                        Note from your Growth Team: {m.note}
                      </p>
                    )}
                    {m.updated_at && (
                      <div className="text-[11px] text-slate-400 mt-1">
                        Updated {new Date(m.updated_at).toLocaleString()}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <p className="mt-8 text-center text-xs text-slate-400">
              This is a private, non-indexed link. Bookmark it — we'll email you on every milestone change.
            </p>
          </>
        )}
      </main>
      <Footer />
      {data && data.public_token && (
        <LaunchChatWidget publicToken={data.public_token}
                           businessName={data.business_name} />
      )}
    </div>
  );
}
